// src/components/CosmicClock/LiveClockPanel.jsx
import React, { useRef } from "react";
import useLiveEphemeris from "../../hooks/useLiveEphemeris";
import useTransitData    from "../../hooks/useTransitData";
import ClockFace from "./ClockFace";
import ClockHands from "./ClockHands";
import EyeCenter from "./EyeCenter"; // if you’ve created this subcomponent
import ClockLabels from "./ClockLabels";
import { standardPlanets } from "../../utils/astroUtils"; 
import { useLanguage } from "../../context/LanguageContext";
import "./LiveClockPanel.css";

const LiveClockPanel = ({ planetPositions }) => {
  const svgRef       = useRef(null);
  const containerRef = useRef(null);
  const size = 800;
  const center = size / 2;
  const radius = center - 20;
  const { t } = useLanguage();

  const { data, loading, error } = useLiveEphemeris();
  const { transitData }          = useTransitData();

  if (loading) return <p>🔄 {t('Loading...')}</p>;
  if (error)   return <p>❌ {t('No data found')}: {error.message}</p>;
  if (!data?.planets?.length) return <p>⚠️ {t('No data found')}.</p>;

  const planetAngles = {};
  const planetData   = {};
  data.planets.filter(p => standardPlanets.includes(p.name)).forEach((planet) => {
    if (!planet.rashiDegreeDMS) return;
    planetAngles[planet.name] = planet.degree;
    // Merge transitInfo from separate async fetch
    planetData[planet.name] = {
      ...planet,
      transitInfo: transitData?.[planet.name] || null,
    };
  });

  return (
    <div className="live-clock-panel">
      <h2 className="live-title">
        <span className="live-dot" />
        {t('Live Cosmic Clock')}
      </h2>

      <div ref={containerRef} className="clock-wrapper" style={{ position:'relative' }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox="-40 -40 880 900"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <filter
              id="glass-light"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur
                in="SourceGraphic"
                stdDeviation="2"
                result="blur"
              />
              <feColorMatrix
                in="blur"
                type="matrix"
                values="1 0 0 0 0  
                        0 1 0 0 0  
                        0 0 1 0 0  
                        0 0 0 0.3 0"
              />
              <feBlend in="SourceGraphic" in2="blur" mode="normal" />
            </filter>
          </defs>

          {/* Clock Components */}
          <ClockFace center={center} radius={radius} size={size} />
          <ClockLabels center={center} radius={radius} />
          <ClockHands
            center={center}
            radius={radius}
            planetAngles={planetAngles}
            planetData={planetData}
            isLive={true}
            svgRef={svgRef}
            containerRef={containerRef}
          />
          <EyeCenter center={center} eyeRadius={30} />
        </svg>
      </div>
    </div>
  );
};

export default LiveClockPanel;
